import React from "react";
import { ModalCompAdvance, ButtonComp, CardComp } from "../../../../components";
import { useViewMemberQuery } from "../../../../store/admin/memberManagement/memberSlice";
import FallbackImage from "../../../../components/Common/ImageWrapper";
import moment from "moment";
import { Spinner } from "react-bootstrap";
import { formatMoney } from "../../../../utils/currencyFormat";

const PendingMemberDetailsModal = ({
  show,
  handleClose,
  memberId,
  onApprove,
  onDecline,
  isProcessing,
}) => {
  const { data: memberDetails, isLoading, error } = useViewMemberQuery(
    memberId,
    {
      skip: !memberId || !show,
    }
  );

  if (!show) return null;

  const formatDate = (date) => {
    if (!date) return "N/A";
    return moment(date).format("DD-MMM-YYYY");
  };

  const DetailRow = ({ label, value, copyable = false }) => (
    <div className="mb-3">
      <p className="mb-1" style={{ fontSize: "13px", color: "#525252" }}>
        {label}
      </p>
      <p
        className="mb-0 fw-semibold"
        style={{ fontSize: "14px", color: "#45464e" }}
      >
        {value || "N/A"}
      </p>
    </div>
  );

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "N/A";
    return formatMoney(Number(amount).toFixed(2));
  };

  return (
    <ModalCompAdvance
      show={show}
      handleClose={handleClose}
      size="xl"
      centered
      close={true}
      modalBody={0}
      arrayComp={[
        {
          name: "Member Details",
          component: (
            <div className="p-4">
              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">Loading member details...</p>
                </div>
              ) : error ? (
                <div className="text-center py-5">
                  <p className="text-danger">
                    Failed to load member details. Please try again.
                  </p>
                </div>
              ) : memberDetails ? (
                <>
                  {/* Header Section */}
                  <div className="text-center mb-4 pb-4 border-bottom">
                    <div className="d-flex justify-content-center mb-3">
                      <FallbackImage
                        src={memberDetails?.photo || "/Staff/avatar.svg"}
                        width="100px"
                        height="100px"
                        className="rounded-circle border border-3"
                        style={{ borderColor: "var(--color-primary)" }}
                      />
                    </div>
                    <h4 className="fw-bold mb-1" style={{ color: "#383838" }}>
                      {memberDetails?.first_name} {memberDetails?.last_name}
                    </h4>
                    <p className="text-muted mb-0">
                      Membership Number: {memberDetails?.member_no || "N/A"}
                    </p>
                  </div>

                  <div className="row g-4">
                    {/* Personal Information */}
                    <div className="col-md-6">
                      <CardComp
                        cardClassName="border-0 shadow-sm h-100"
                        cardBodyClassName="p-4"
                      >
                        <h5
                          className="fw-bold mb-4 pb-3 border-bottom"
                          style={{ fontSize: "16px", color: "#383838" }}
                        >
                          Personal Information
                        </h5>
                        <DetailRow
                          label="Title"
                          value={memberDetails?.title?.name}
                        />
                        <DetailRow
                          label="First Name"
                          value={memberDetails?.first_name}
                        />
                        <DetailRow
                          label="Other Name"
                          value={memberDetails?.other_name}
                        />
                        <DetailRow
                          label="Last Name"
                          value={memberDetails?.last_name}
                        />
                        <DetailRow
                          label="Date of Birth"
                          value={formatDate(memberDetails?.dob)}
                        />
                        <DetailRow
                          label="Gender"
                          value={memberDetails?.gender?.name}
                        />
                        <DetailRow
                          label="Marital Status"
                          value={memberDetails?.marital_status?.name}
                        />
                        <DetailRow
                          label="Occupation"
                          value={memberDetails?.occupation}
                        />
                      </CardComp>
                    </div>

                    {/* Contact Information */}
                    <div className="col-md-6">
                      <CardComp
                        cardClassName="border-0 shadow-sm h-100"
                        cardBodyClassName="p-4"
                      >
                        <h5
                          className="fw-bold mb-4 pb-3 border-bottom"
                          style={{ fontSize: "16px", color: "#383838" }}
                        >
                          Contact Information
                        </h5>
                        <DetailRow
                          label="Email Address"
                          value={memberDetails?.email}
                        />
                        <DetailRow
                          label="Phone Number"
                          value={memberDetails?.phone}
                        />
                        <DetailRow
                          label="Full Address"
                          value={memberDetails?.address}
                        />
                        <DetailRow
                          label="City/State"
                          value={memberDetails?.city?.name}
                        />
                        <DetailRow
                          label="Country"
                          value={memberDetails?.country?.name}
                        />
                        {memberDetails?.city?.landmark && (
                          <DetailRow
                            label="Landmark"
                            value={memberDetails?.city?.landmark}
                          />
                        )}
                      </CardComp>
                    </div>

                    {/* Next of Kin Information */}
                    <div className="col-md-6">
                      <CardComp
                        cardClassName="border-0 shadow-sm h-100"
                        cardBodyClassName="p-4"
                      >
                        <h5
                          className="fw-bold mb-4 pb-3 border-bottom"
                          style={{ fontSize: "16px", color: "#383838" }}
                        >
                          Next of Kin
                        </h5>
                        <DetailRow
                          label="Name"
                          value={memberDetails?.next_of_kin_name}
                        />
                        <DetailRow
                          label="Phone Number"
                          value={memberDetails?.next_of_kin_phone}
                        />
                        <DetailRow
                          label="Relationship"
                          value={
                            memberDetails?.relationship_status?.name
                          }
                        />
                      </CardComp>
                    </div>

                    {/* Branch & Registration Information */}
                    <div className="col-md-6">
                      <CardComp
                        cardClassName="border-0 shadow-sm h-100"
                        cardBodyClassName="p-4"
                      >
                        <h5
                          className="fw-bold mb-4 pb-3 border-bottom"
                          style={{ fontSize: "16px", color: "#383838" }}
                        >
                          Branch & Registration
                        </h5>
                        <DetailRow
                          label="Branch"
                          value={memberDetails?.branch?.name}
                        />
                        <DetailRow
                          label="Registration Date"
                          value={formatDate(memberDetails?.created_at)}
                        />
                        {(memberDetails?.referred_by?.name ||
                          memberDetails?.referred_by?.member_no) && (
                          <DetailRow
                            label="Referred by"
                            value={
                              <>
                                {memberDetails.referred_by.name || "Member"}
                                {memberDetails.referred_by.member_no
                                  ? ` · ${memberDetails.referred_by.member_no}`
                                  : ""}
                              </>
                            }
                          />
                        )}
                        <DetailRow
                          label="Status"
                          value={
                            <span
                              className="badge rounded-pill px-3 py-2"
                              style={{
                                backgroundColor: "#ff9500",
                                color: "white",
                                fontSize: "12px",
                              }}
                            >
                              PENDING
                            </span>
                          }
                        />
                      </CardComp>
                    </div>

                    {/* Registration Fee Information - Always show for pending members */}
                    <div className="col-12">
                        <CardComp
                          cardClassName="border-0 shadow-sm"
                          cardBodyClassName="p-4"
                        >
                          <h5
                            className="fw-bold mb-4 pb-3 border-bottom"
                            style={{ fontSize: "16px", color: "#383838" }}
                          >
                            Registration Fee & Payment
                          </h5>
                          <div className="row">
                            <div className="col-md-4">
                              <DetailRow
                                label="Registration Fee"
                                value={formatCurrency(
                                  memberDetails?.registration_fee ||
                                    memberDetails?.registration_payment?.amount
                                )}
                              />
                            </div>
                            <div className="col-md-4">
                              <DetailRow
                                label="Payment Method"
                                value={
                                  memberDetails?.payment_method
                                    ? memberDetails.payment_method
                                        .split("_")
                                        .map(
                                          (word) =>
                                            word.charAt(0).toUpperCase() +
                                            word.slice(1).toLowerCase()
                                        )
                                        .join(" ")
                                    : memberDetails?.registration_payment
                                        ?.payment_method
                                    ? memberDetails.registration_payment.payment_method
                                        .split("_")
                                        .map(
                                          (word) =>
                                            word.charAt(0).toUpperCase() +
                                            word.slice(1).toLowerCase()
                                        )
                                        .join(" ")
                                    : "N/A"
                                }
                              />
                            </div>
                            <div className="col-md-4">
                              <DetailRow
                                label="Payment Status"
                                value={
                                  memberDetails?.registration_payment
                                    ?.payment_status ? (
                                    <span
                                      className="badge rounded-pill px-3 py-2"
                                      style={{
                                        backgroundColor:
                                          memberDetails.registration_payment
                                            .payment_status === "PAID"
                                            ? "#19bc31"
                                            : "#ff9500",
                                        color: "white",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {memberDetails.registration_payment.payment_status}
                                    </span>
                                  ) : memberDetails?.is_paid ? (
                                    <span
                                      className="badge rounded-pill px-3 py-2"
                                      style={{
                                        backgroundColor: "#19bc31",
                                        color: "white",
                                        fontSize: "12px",
                                      }}
                                    >
                                      PAID
                                    </span>
                                  ) : (
                                    <span
                                      className="badge rounded-pill px-3 py-2"
                                      style={{
                                        backgroundColor: "#ff9500",
                                        color: "white",
                                        fontSize: "12px",
                                      }}
                                    >
                                      PENDING
                                    </span>
                                  )
                                }
                              />
                            </div>
                            {memberDetails?.registration_payment
                              ?.payment_date && (
                              <div className="col-md-4">
                                <DetailRow
                                  label="Payment Date"
                                  value={formatDate(
                                    memberDetails.registration_payment
                                      .payment_date
                                  )}
                                />
                              </div>
                            )}
                            {memberDetails?.registration_payment
                              ?.transaction_reference && (
                              <div className="col-md-4">
                                <DetailRow
                                  label="Transaction Reference"
                                  value={
                                    memberDetails.registration_payment
                                      .transaction_reference
                                  }
                                />
                              </div>
                            )}
                          </div>
                        </CardComp>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="row mt-4 pt-4 border-top">
                    <div className="col-12">
                      <div className="d-flex gap-3 justify-content-end">
                        <ButtonComp
                          onClick={handleClose}
                          btnText="Close"
                          btnClassName="px-4 py-2 fw-semibold rounded"
                          style={{
                            backgroundColor: "#ffffff",
                            border: "1px solid #e0e0e0",
                            color: "#45464e",
                            fontSize: "14px",
                          }}
                          disabled={isProcessing}
                        />
                        <ButtonComp
                          onClick={onDecline}
                          btnText={isProcessing ? "Processing..." : "Decline"}
                          btnClassName="px-4 py-2 fw-semibold rounded"
                          style={{
                            backgroundColor: "#ffffff",
                            border: "1px solid #e0e0e0",
                            color: "#45464e",
                            fontSize: "14px",
                          }}
                          disabled={isProcessing}
                        />
                        <ButtonComp
                          onClick={onApprove}
                          btnText={isProcessing ? "Processing..." : "Approve"}
                          btnClassName="px-4 py-2 fw-semibold text-white rounded"
                          style={{
                            backgroundColor: "var(--color-primary)",
                            border: "none",
                            fontSize: "14px",
                          }}
                          disabled={isProcessing}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          ),
        },
      ]}
    />
  );
};

export default PendingMemberDetailsModal;

